import {Box, BoxProps, Grid, GridItem, GridProps, useMultiStyleConfig} from '@chakra-ui/react'
import {CardPhotoVideo} from '_uiComponents'
import React, {useEffect, useRef, useState} from 'react'
import {DoubleSameItem} from './partials/double-same-item'
import {CardPhotoVideoObject} from '_types'
import {useAnalytics, useIsElementInViewport} from '_customHooks'
import {PAGE_DESIGNER_MODULES} from '_utils'
import {toEditorialAnalyticsList} from '_analytics/utils/analyticsUtils'

interface DoubleSameProps {
    leftElement?: CardPhotoVideoObject
    rightElement?: CardPhotoVideoObject
    classes?: string
    extraCSS?: string
    backgroundColor?: string
    isMobileRow?: boolean
    isBeforeRightMobile?: boolean
    isRightCarousel?: boolean
    isLeftCarousel?: boolean
    rightItem?: string[]
    leftItem?: string[]
    analyticsID?: string
    position: number
    analyticsList?: string
    analyticsName?: string
    adaptiveHeight?: boolean
}

/**
 * DoubleSame component renders a container for two possible items displayed in row.
 *
 * @param {CardPhotoVideoObject} leftElement - The object for the left element
 * @param {CardPhotoVideoObject} rightElement - The object for the right element
 * @param {string} classes - The possible extra classes to add to element.
 * @param {string} extraCSS - The extra CSS asociated to the container.
 * @param {string} backgroundColor - The hexadecimal for the color background for the container.
 * @param {boolean} isMobileRow - It indicates the possibility for the components to be displayed in a row when the window is mobile size: default they display in column
 * @param {boolean} isBeforeRightMobile - It indicates if the right element when in mobile screen size will take first position.
 * @param {boolean} isRightCarousel - It indicates if right element must consider the product data set
 * @param {boolean} isleftCarousel - It indicates if left element must consider the product data set
 * @param {string[]} leftItem - An object with the attributes for the left product
 * @param {string[]} rightItem - An object with the attributes for the right product
 * @param {string} props.analyticsID - The analytics id to send for the tracking
 * @param {number} props.position - The position of the element with respect to other page designer elements in the page
 * @param {string} props.analyticsList - PLP analyticsList (normalized to editorial for Page Designer).
 * @param {string} [props.analyticsName] - Optional analytics name for promo events.
 * @param {boolean} [props.adaptiveHeight] - Optional flag to adapt module height to its container.
 * @returns {JSX.Element} The rendered DoubleSame component.
 */
const DoubleSame = ({
    isMobileRow = false,
    leftElement,
    rightElement,
    classes,
    extraCSS,
    backgroundColor,
    isBeforeRightMobile,
    isLeftCarousel,
    isRightCarousel,
    leftItem,
    rightItem,
    analyticsID,
    position,
    analyticsList,
    analyticsName,
    adaptiveHeight,
}: DoubleSameProps): JSX.Element => {
    const styles = useMultiStyleConfig('DoubleSame', {isMobileRow, backgroundColor, isBeforeRightMobile, adaptiveHeight}) as Record<
        string,
        BoxProps & GridProps
    >
    const {analyticsEventPromoView, analyticsEventPromoClick} = useAnalytics()
    const targetElement = useRef(null)
    const isVisible = useIsElementInViewport(targetElement)
    const [alreadySent, setAlreadySent] = useState(false)
    const analyticsListEditorial = toEditorialAnalyticsList(analyticsList)
    const triggerAnalyticsEventPromoClick = (textBtn: string, positionBtn?: string): void => {
        analyticsEventPromoClick({
            id: analyticsID ? analyticsID : 'null',
            position: positionBtn ? String(position) + '-' + positionBtn : String(position),
            creative: PAGE_DESIGNER_MODULES.DOUBLE_SAME_CLOUDINARY + ' - ' + leftElement?.layout + ' - ' + (leftElement?.isImgZoom ? 'ANIMATION' : 'STATIC'),
            promoText: textBtn,
            name: analyticsName ? analyticsName : 'null',
        })
    }

    const buildSubItem = (
        positionItem: string,
        style: GridProps,
        isCarousel?: boolean,
        item?: string[],
        element?: CardPhotoVideoObject,
        isOnlyElements?: boolean,
    ) => {
        return (
            <GridItem
                {...style}
                data-testid={`double-same-${positionItem}${isCarousel ? '-carousel' : '-photo'}`}
                id={`double-same-${positionItem}`}
                className={`cc-object-container cc-object-${positionItem}`}
            >
                {isCarousel && item && item.length ? (
                    <Box className="cc-pd-slider-double-same" {...styles.itemWrapper}>
                        <DoubleSameItem
                            productsIdList={item}
                            title={element?.title}
                            text={element?.text}
                            id={String(position) + '-' + positionItem}
                            analyticsList={analyticsListEditorial}
                        />
                    </Box>
                ) : (
                    <CardPhotoVideo
                        propsObject={{...element, isLeft: positionItem === 'left'}}
                        classes={classes}
                        isVisible={isVisible}
                        idProductCta={positionItem === 'left' ? '0' : '1'}
                        onClick={() => {
                            triggerAnalyticsEventPromoClick(
                                element && element.ctaText ? element.ctaText : '',
                                isOnlyElements ? (positionItem === 'left' ? '1' : '2') : '',
                            )
                        }}
                        analyticsList={analyticsListEditorial}
                        position={position}
                        isDoubleSame={true}
                        adaptiveHeight={adaptiveHeight}
                    />
                )}
            </GridItem>
        )
    }

    useEffect(() => {
        if (isVisible && !alreadySent) {
            analyticsEventPromoView({
                id: analyticsID ? analyticsID : 'null',
                position: position,
                creative:
                    PAGE_DESIGNER_MODULES.DOUBLE_SAME_CLOUDINARY + ' - ' + leftElement?.layout + ' - ' + (leftElement?.isImgZoom ? 'ANIMATION' : 'STATIC'),
                name: analyticsName ? analyticsName : 'null',
            })
            setAlreadySent(true)
        }
    }, [isVisible])

    return (
        <Box {...styles.wrapper} data-testid="double-same" css={extraCSS} ref={targetElement}>
            <Grid {...styles.grid} className={`${classes} cc-pd cc-pd-double-same`} data-items-layout={leftElement?.layout}>
                {buildSubItem('left', styles.gridItemLeft, isLeftCarousel, leftItem, leftElement, !(isLeftCarousel || isRightCarousel))}
                {buildSubItem('right', styles.gridItemRight, isRightCarousel, rightItem, rightElement, !(isLeftCarousel || isRightCarousel))}
            </Grid>
        </Box>
    )
}

export {DoubleSame}
